import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
import { ModulesService } from '@shared/services/modules.service';

import { LoggerService } from '@app/logger/logger.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { NetworkService } from '@app/shared/services/network.service';
import { TabService } from '@app/shared/services/tab-check.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { UploadFileDialogService } from '@app/upload-file-dialog/upload-file-dialog.service';
import { TableStructureModel } from '@app/shared/models/table.model';

import { UsersService } from '@app/shared/services/entities/users.service';
import { WarehousesService } from '@app/shared/services/entities/warehouse.service';
import { PlacesService } from '@app/shared/services/entities/places.service';
import { ProductsService } from '@app/shared/services/entities/products.service';
import { StockService } from '@app/shared/services/entities/stock.service';
import { ShippingsService } from '@app/shared/services/entities/shippings.service';
import { InterventionsService } from '@app/shared/services/entities/interventions.service';
import { InventoryMovementsService } from '@app/shared/services/entities/inventoryMovements.service';

const log = new LoggerService('MOVIMIENTO DE INSUMOS');

/** Por qué se busca: etiqueta RFID, código de producto o lote. */
export type TermType = 'tag' | 'code' | 'batch';

export interface SearchTerm { type: TermType; value: string; }

/** Un renglón de la línea de tiempo de una pieza. */
export interface MoveRow {
  date: string;                      // texto original (para mostrar)
  time: number;                      // para ordenar
  kind: 'bitacora' | 'vale' | 'cirugia';
  label: string;
  detail: string;
  document: string;
  user: string;
}

export interface Piece {
  _open?: boolean;
  id: string;
  tagRfid: string;
  code: string;
  description: string;
  batch: string;
  deliveryFolio?: string;
  expiration: string;
  status: string;
  warehouse: string;
  place: string;
  moves: MoveRow[];
  editing: boolean;
  newStatus: string;
}

/** Límite de piezas por consulta: buscar por código o lote puede traer miles. */
const MAX_PIECES = 500;
const PAGE = 60;

const MOVE_LABELS: Record<string, string> = {
  ENTRADA: 'Entrada', ETIQUETADO: 'Etiquetado', SALIDA: 'Salida', TRASPASO: 'Traspaso',
  CONSUMO: 'Consumo', DEVOLUCION: 'Devolución', MERMA: 'Merma', AJUSTE: 'Ajuste', CONTEO: 'Conteo',
  'NO CONFORME': 'No conforme',
};

/**
 * Auditoría → Movimiento de insumos.
 *
 * Antes era "Movimientos de Etiqueta" y solo aceptaba etiquetas RFID. Ahora se
 * puede buscar por ETIQUETA, CÓDIGO de producto o LOTE (varios a la vez, a mano
 * o por Excel). Por cada pieza encontrada arma una línea de tiempo con:
 *   · la bitácora de inventario (entradas, traspasos, consumos, mermas…),
 *   · los vales / traspasos (shippings) donde viajó la pieza,
 *   · los folios de cirugía donde se entregó, consumió o devolvió.
 */
import { ListSelection, ListView } from '@app/shared/utils/list-view';
@Component({
  standalone: true,
  imports: [PageLoaderComponent, SearchPillComponent, PageTitleComponent, FormsModule, ReactiveFormsModule, MaterialModule],
  selector: 'app-rfid-movements',
  templateUrl: './rfid-movements.component.html',
  styleUrls: ['../audit-page.scss', './rfid-movements.component.scss'],
})
export class RFIDMovementComponent implements OnInit, OnDestroy {
  readonly listView = new ListView('audit-rfid-movements', 'cards');
  /** Título = etiqueta visible del módulo (tabla `modules`). */
  readonly pageLabel = inject(ModulesService).moduleTitle('/audit/rfid', 'Movimiento de insumos');

  private readonly router = inject(Router);
  private readonly customMessage = inject(CustomMessageService);
  private readonly networkService = inject(NetworkService);
  private readonly tabWatcher = inject(TabService);
  private readonly simpleExcel = inject(SimpleExcelService);
  private readonly uploadFileDialog = inject(UploadFileDialogService);
  private readonly usersService = inject(UsersService);
  private readonly warehousesService = inject(WarehousesService);
  private readonly placesService = inject(PlacesService);
  private readonly productsService = inject(ProductsService);
  private readonly stockService = inject(StockService);
  private readonly shippingsService = inject(ShippingsService);
  private readonly interventionsService = inject(InterventionsService);
  private readonly movementsService = inject(InventoryMovementsService);

  isSlowConnection = false;
  private sub?: Subscription;

  // ── Búsqueda ──
  private readonly modulesService = inject(ModulesService);
  /** Ícono del catálogo de Productos (tabla `modules`); se usa en "Código". */
  productsIcon = 'qr_code_2';
  termTypes: { id: TermType; label: string; icon: string; hint: string }[] = [
    { id: 'tag',   label: 'RFID',     icon: 'contactless', hint: 'Etiqueta RFID (24 caracteres)' },
    { id: 'code',  label: 'Código',   icon: 'qr_code_2',   hint: 'Código de insumo (CFN)' },
    { id: 'batch', label: 'Lote',     icon: 'inventory_2', hint: 'Número de lote' },
  ];
  termType: TermType = 'tag';
  termValue = '';
  terms: SearchTerm[] = [];

  loading = false;
  searched = false;
  truncated = false;
  notFoundCodes: string[] = [];

  // ── Resultados ──
  pieces: Piece[] = [];
  filterText = '';
  statusFilter: string | null = null;
  visible = PAGE;

  // ── Catálogos para nombres legibles ──
  private products: any[] = [];
  private productById: Record<string, any> = {};
  private warehouseById: Record<string, any> = {};
  private placeById: Record<string, any> = {};
  private userById: Record<string, string> = {};

  isAdmin = false;

  /** Toma el ícono que tenga el catálogo de Productos en Módulos. */
  private async loadProductsIcon(): Promise<void> {
    try {
      const r: any = await this.modulesService.search({ searchType: 'default', condition: { deleted: false } });
      const mod = ((r?.data ?? []) as any[]).find(m => String(m?.route ?? '').replace(/\/+$/, '').toLowerCase() === '/catalogues/products');
      if (mod?.icon) {
        this.productsIcon = mod.icon;
        this.termTypes = this.termTypes.map(t => t.id === 'code' ? { ...t, icon: mod.icon } : t);
      }
    } catch { /* se queda el ícono por omisión */ }
  }

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();

  ngOnInit(): void {
    this.networkService.isConnectionSlow$.subscribe(slow => this.isSlowConnection = slow);
    this.sub = this.tabWatcher.watchSamePage().subscribe(event => {
      if (event === 'open') {
        this.customMessage.open({ title: 'Advertencia', type: 'error', message: 'Este módulo está abierto en múltiples pestañas. Para garantizar la funcionalidad del sistema, por favor cierre las demás pestañas y espere 5 segundos.', loading: true });
      } else if (event === 'close') {
        this.customMessage.close();
      }
    });
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    void this.loadProductsIcon();
    await Promise.all([this.loadRole(), this.loadCatalogs()]);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.tabWatcher.stopWatching();
  }

  private async loadRole(): Promise<void> {
    try {
      const response: any = await this.usersService.getRolNameByUserId({ id: sessionStorage.getItem('userId') });
      const roles = response?.data?.[0]?.roles ?? [];
      this.isAdmin = (Array.isArray(roles) ? roles : [roles]).some((r: any) => String(r).includes('Administrador'));
    } catch { this.isAdmin = false; }
  }

  private async loadCatalogs(): Promise<void> {
    const list = async (service: any): Promise<any[]> => {
      try {
        const r: any = await service.search({ searchType: 'default', condition: { deleted: false } });
        return ((r?.data as any[]) ?? []).filter(x => x);
      } catch { return []; }
    };
    const [products, warehouses, places, users] = await Promise.all([
      list(this.productsService), list(this.warehousesService), list(this.placesService), list(this.usersService),
    ]);
    this.products = products;
    this.productById = Object.fromEntries(products.map(p => [p.id, p]));
    this.warehouseById = Object.fromEntries(warehouses.map(w => [w.id, w]));
    this.placeById = Object.fromEntries(places.map(p => [p.id, p]));
    this.userById = Object.fromEntries(users.map(u => [u.id, u.name ?? u.email ?? '']));
  }

  // ─── Términos de búsqueda ───────────────────────────────────────────────

  get currentHint(): string { return this.termTypes.find(t => t.id === this.termType)?.hint ?? ''; }

  typeLabel(type: TermType): string { return this.termTypes.find(t => t.id === type)?.label ?? type; }

  private clean(value: any): string {
    return String(value ?? '').replace(/[<>"'&]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();
  }

  /** Agrega el valor escrito. Acepta varios separados por coma o espacio. */
  addTerm(): void {
    const values = this.termValue.split(/[,\n;]+/).map(v => this.clean(v)).filter(v => !!v);
    if (values.length === 0) return;
    for (const value of values) {
      if (!this.pushTerm(this.termType, value)) return;
    }
    this.termValue = '';
  }

  private pushTerm(type: TermType, value: string, silent = false): boolean {
    if (type === 'tag' && value.length !== 24) {
      if (!silent) this.customMessage.open({ title: 'RFID inválido', type: 'error', message: `El RFID <b>${value}</b> debe tener 24 caracteres.`, duration: 2500 });
      return false;
    }
    if (this.terms.some(t => t.type === type && t.value === value)) return true;
    this.terms.push({ type, value });
    return true;
  }

  removeTerm(index: number): void {
    if (this.loading) return;
    this.terms.splice(index, 1);
  }

  reset(): void {
    this.terms = [];
    this.termValue = '';
    this.pieces = [];
    this.searched = false;
    this.truncated = false;
    this.notFoundCodes = [];
    this.filterText = '';
    this.statusFilter = null;
    this.visible = PAGE;
  }

  // ─── Búsqueda ───────────────────────────────────────────────────────────

  async search(): Promise<void> {
    if (this.termValue.trim()) this.addTerm();
    if (this.terms.length === 0) return;
    this.loading = true;
    this.searched = true;
    this.truncated = false;
    this.notFoundCodes = [];
    this.pieces = [];
    this.visible = PAGE;
    try {
      const values = (type: TermType) => this.terms.filter(t => t.type === type).map(t => t.value);
      const tags = values('tag');
      const batches = values('batch');
      const codes = values('code');

      // Código → ids de producto (el stock guarda el id, no el código).
      const productIds: string[] = [];
      for (const code of codes) {
        const found = this.products.filter(p => this.clean(p.code) === code);
        if (found.length === 0) this.notFoundCodes.push(code);
        productIds.push(...found.map(p => p.id));
      }

      const queries: Promise<any>[] = [];
      if (tags.length) queries.push(this.stockService.search({ searchType: 'default', condition: { deleted: false, tagRfid: tags } }));
      if (batches.length) queries.push(this.stockService.search({ searchType: 'default', condition: { deleted: false, batch: batches } }));
      if (productIds.length) queries.push(this.stockService.search({ searchType: 'default', condition: { deleted: false, product: productIds } }));
      const responses = await Promise.all(queries);

      const byId = new Map<string, any>();
      for (const r of responses) {
        for (const item of ((r?.data as any[]) ?? [])) if (item?.id) byId.set(item.id, item);
      }
      let stock = [...byId.values()];
      if (stock.length > MAX_PIECES) { stock = stock.slice(0, MAX_PIECES); this.truncated = true; }

      if (stock.length === 0) {
        this.loading = false;
        return;
      }

      const ids = stock.map(s => s.id);
      const [bitacora, shippings, interventions] = await Promise.all([
        this.loadBitacora(ids), this.loadShippings(), this.loadInterventions(),
      ]);

      this.pieces = stock.map(s => this.buildPiece(s, bitacora, shippings, interventions))
        .sort((a, b) => (b.moves[0]?.time ?? 0) - (a.moves[0]?.time ?? 0));
    } catch (err) {
      log.error('[search]', err);
      await this.customMessage.open({ title: 'Error', type: 'error', message: 'No se pudo completar la búsqueda. ' + String(err), dismissBtn: true });
    } finally {
      this.loading = false;
    }
  }

  /** Bitácora de las piezas (en bloques para no mandar listas enormes). */
  private async loadBitacora(ids: string[]): Promise<any[]> {
    const rows: any[] = [];
    for (let i = 0; i < ids.length; i += 200) {
      try {
        const r: any = await this.movementsService.search({ searchType: 'default', condition: { deleted: false, stock: ids.slice(i, i + 200) } });
        rows.push(...(((r?.data as any[]) ?? []).filter(x => x)));
      } catch (err) { log.error('[bitacora]', err); }
    }
    return rows;
  }

  private async loadShippings(): Promise<any[]> {
    try {
      const r: any = await this.shippingsService.search({ searchType: 'default', condition: { deleted: false } });
      return ((r?.data as any[]) ?? []).filter(x => x);
    } catch { return []; }
  }

  private async loadInterventions(): Promise<any[]> {
    try {
      const r: any = await this.interventionsService.search({ searchType: 'default', condition: { deleted: false } });
      return ((r?.data as any[]) ?? []).filter(x => x);
    } catch { return []; }
  }

  /** ¿La lista (de ids u objetos {id|stock}) contiene la pieza? */
  private has(list: any, id: string): boolean {
    if (!Array.isArray(list)) return false;
    return list.some(x => x === id || x?.id === id || x?.stock === id);
  }

  private toTime(value: any): number {
    if (!value) return 0;
    const t = Date.parse(String(value));
    return isNaN(t) ? 0 : t;
  }

  private whName(id: any): string {
    if (!id) return '';
    if (id === 'EXTERNO') return 'Externo';
    const w = this.warehouseById[id];
    if (!w) return String(id);
    const alias = this.placeAlias(w.place);
    // Sin repetir cuando el almacén se llama igual que el alias del sitio.
    return alias && this.clean(alias) !== this.clean(w.name) ? `${w.name} (${alias})` : w.name;
  }

  private placeAlias(placeId: any): string {
    const place = this.placeById[placeId];
    return place?.alias || place?.name || '';
  }

  /** "A → B", "Hacia B" o "Desde A", según lo que traiga el movimiento. */
  private route(fromId: any, toId: any): string {
    const from = this.whName(fromId), to = this.whName(toId);
    if (from && to) return `${from} → ${to}`;
    if (to) return `Hacia ${to}`;
    if (from) return `Desde ${from}`;
    return '';
  }

  /** "Disponible → Consumido", o solo el estado final si no hubo cambio. */
  private statusChange(before: any, after: any): string {
    if (before && after && before !== after) return `${before} → ${after}`;
    const final = after || before;
    return final ? `Estado: ${final}` : '';
  }

  private buildPiece(s: any, bitacora: any[], shippings: any[], interventions: any[]): Piece {
    const product = this.productById[s.product];
    const moves: MoveRow[] = [];

    for (const m of bitacora.filter(m => m.stock === s.id)) {
      const route = this.route(m.fromWarehouse, m.toWarehouse);
      const status = this.statusChange(m.statusBefore, m.statusAfter);
      const date = m.date ?? m.createdOn;
      moves.push({ date, time: this.toTime(date), kind: 'bitacora',
        label: MOVE_LABELS[m.type] ?? m.type ?? 'Movimiento',
        detail: [route, status].filter(x => !!x).join(' · '),
        document: m.document ?? '', user: this.userById[m.user ?? m.createdBy] ?? '' });
    }

    for (const sh of shippings.filter(sh => this.has(sh.products, s.id) || this.has(sh.items, s.id))) {
      moves.push({ date: sh.createdOn, time: this.toTime(sh.createdOn), kind: 'vale',
        label: sh.type ? `Vale · ${sh.type}` : 'Vale / traspaso',
        detail: this.route(sh.fromWarehouse, sh.toWarehouse),
        document: sh.fileNumber ?? '', user: this.userById[sh.createdBy] ?? '' });
    }

    for (const iv of interventions) {
      const p = iv.products ?? {};
      const delivered = this.has(p.delivery, s.id), consumed = this.has(p.consumption, s.id), refunded = this.has(p.refund, s.id);
      if (!delivered && !consumed && !refunded) continue;
      const flags = [delivered && 'Entregada', consumed && 'Consumida', refunded && 'Devuelta'].filter(x => !!x).join(' · ');
      const date = iv.interventionDatetime ?? iv.createdOn;
      moves.push({ date, time: this.toTime(date), kind: 'cirugia',
        label: 'Salida a cirugía', detail: `${flags}${iv.status ? ' · Folio ' + String(iv.status).toLowerCase() : ''}`,
        document: iv.fileNumber ?? '', user: this.userById[iv.technician ?? iv.createdBy] ?? '' });
    }

    moves.sort((a, b) => b.time - a.time);
    const wh = this.warehouseById[s.warehouse];
    const placeLabel = this.placeAlias(wh?.place ?? s.place);
    return {
      id: s.id,
      tagRfid: s.tagRfid ?? '',
      code: product?.code ?? '—',
      description: product?.description ?? '',
      batch: s.batch ?? '',
      deliveryFolio: s.deliveryFolio ?? '',
      expiration: s.expiration ?? '',
      status: s.status ?? '—',
      warehouse: wh?.name ?? '',
      // Sin repetir cuando el almacén se llama igual que el alias del sitio.
      place: placeLabel && this.clean(placeLabel) !== this.clean(wh?.name) ? placeLabel : '',
      moves,
      editing: false,
      newStatus: s.status ?? '',
    };
  }

  // ─── Resultados: filtros e indicadores ─────────────────────────────────

  get filtered(): Piece[] {
    const needle = this.clean(this.filterText);
    return this.pieces.filter(p =>
      (!this.statusFilter || p.status === this.statusFilter) &&
      (!needle || [p.tagRfid, p.code, p.description, p.batch, p.warehouse, p.place].some(v => this.clean(v).includes(needle))));
  }

  get shown(): Piece[] { return this.filtered.slice(0, this.visible); }

  showMore(): void { this.visible += PAGE; }

  get kpis() {
    return {
      pieces: this.pieces.length,
      products: new Set(this.pieces.map(p => p.code)).size,
      batches: new Set(this.pieces.map(p => p.batch).filter(b => !!b)).size,
      moves: this.pieces.reduce((n, p) => n + p.moves.length, 0),
    };
  }

  get statusCounts(): { status: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const p of this.pieces) counts.set(p.status, (counts.get(p.status) ?? 0) + 1);
    return [...counts.entries()].map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);
  }

  toggleStatus(status: string): void {
    this.statusFilter = this.statusFilter === status ? null : status;
    this.visible = PAGE;
  }

  statusClass(status: string): string {
    const s = String(status ?? '').toLowerCase();
    if (s.startsWith('dispon')) return 'st--ok';
    if (s.startsWith('consum')) return 'st--done';
    if (s.startsWith('transit') || s.startsWith('tránsit') || s.startsWith('maleta')) return 'st--move';
    if (s.startsWith('caduc') || s.startsWith('merma')) return 'st--bad';
    return 'st--neutral';
  }

  kindIcon(kind: MoveRow['kind']): string {
    return kind === 'bitacora' ? 'swap_horiz' : kind === 'vale' ? 'local_shipping' : 'medical_services';
  }

  formatDate(value: string): string {
    const t = this.toTime(value);
    if (!t) return value || '—';
    return new Date(t).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  }

  // ─── Cambio de estado (solo administradores) ────────────────────────────

  async saveStatus(piece: Piece): Promise<void> {
    if (!piece.newStatus || piece.newStatus === piece.status) { piece.editing = false; return; }
    try {
      this.customMessage.open({ title: 'Espere', type: 'success', message: 'Actualizando estado...', loading: true });
      const response: any = await this.stockService.update({ id: piece.id, status: piece.newStatus, tagRfid: piece.tagRfid });
      this.customMessage.close();
      if (response?.state === 'success') {
        piece.status = piece.newStatus;
        piece.editing = false;
        await this.customMessage.open({ title: 'Estado actualizado', type: 'success', message: response.message, duration: 2000 });
      } else {
        await this.customMessage.open({ title: 'Error', type: 'error', message: response?.message, dismissBtn: true });
      }
    } catch (err) {
      this.customMessage.close();
      await this.customMessage.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  // ─── Excel ──────────────────────────────────────────────────────────────

  async downloadTemplate(): Promise<void> {
    await this.simpleExcel.simpleXLSX({
      fileName: 'Plantilla - Movimiento de insumos',
      sheets: [{
        sheetName: 'Busqueda', header: [], lineBreak: 0, data: [],
        columns: [
          { columnKey: 'tag', columnName: 'RFID', type: 'string' },
          { columnKey: 'code', columnName: 'CODIGO', type: 'string' },
          { columnKey: 'batch', columnName: 'LOTE', type: 'string' },
        ] as TableStructureModel[],
      }],
    });
  }

  /** Excel con columnas ETIQUETA / CODIGO / LOTE (cualquiera, en cualquier fila). */
  async uploadTerms(): Promise<void> {
    const rows: any = await this.uploadFileDialog.open();
    if (!Array.isArray(rows)) return;
    const norm = (k: string) => k.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase();
    const map: Record<string, TermType> = { ETIQUETA: 'tag', RFID: 'tag', 'ETIQUETA RFID': 'tag', CODIGO: 'code', CFN: 'code', LOTE: 'batch' };
    let added = 0, invalid = 0;
    for (const row of rows) {
      for (const [key, raw] of Object.entries(row ?? {})) {
        const type = map[norm(key)];
        const value = this.clean(raw);
        if (!type || !value) continue;
        if (this.pushTerm(type, value, true)) added++; else invalid++;
      }
    }
    await this.customMessage.open({
      title: 'Archivo cargado', type: invalid ? 'warning' : 'success',
      message: `${added} valor(es) agregados${invalid ? `; ${invalid} RFID ignorado(s) por no tener 24 caracteres` : ''}.`,
      duration: 2500,
    });
  }

  async downloadReport(): Promise<void> {
    const data: any[] = [];
    for (const p of this.filtered) {
      const base = { code: p.code, description: p.description, tagRfid: p.tagRfid, batch: p.batch, expiration: p.expiration, status: p.status, location: [p.warehouse, p.place].filter(x => !!x).join(' · ') };
      if (p.moves.length === 0) data.push({ ...base, date: '', move: 'Sin movimientos', detail: '', document: '', user: '' });
      for (const m of p.moves) data.push({ ...base, date: this.formatDate(m.date), move: m.label, detail: m.detail, document: m.document, user: m.user });
    }
    if (data.length === 0) {
      await this.customMessage.open({ title: 'Información', type: 'neutral', message: 'No hay información para descargar.', duration: 2000 });
      return;
    }
    await this.simpleExcel.simpleXLSX({
      fileName: 'Movimiento de insumos',
      sheets: [{
        sheetName: 'Movimientos', header: ['Movimiento de insumos', 'Epione'], lineBreak: 1, data,
        columns: [
          { columnKey: 'code', columnName: 'Código', type: 'string' },
          { columnKey: 'description', columnName: 'Descripción', type: 'string' },
          { columnKey: 'tagRfid', columnName: 'RFID', type: 'string' },
          { columnKey: 'batch', columnName: 'Lote', type: 'string' },
          { columnKey: 'expiration', columnName: 'Caducidad', type: 'string' },
          { columnKey: 'status', columnName: 'Estado', type: 'string' },
          { columnKey: 'location', columnName: 'Ubicación', type: 'string' },
          { columnKey: 'date', columnName: 'Fecha', type: 'string' },
          { columnKey: 'move', columnName: 'Movimiento', type: 'string' },
          { columnKey: 'detail', columnName: 'Detalle', type: 'string' },
          { columnKey: 'document', columnName: 'Documento', type: 'string' },
          { columnKey: 'user', columnName: 'Usuario', type: 'string' },
        ] as TableStructureModel[],
      }],
    });
  }

  goBack(): void { this.router.navigate(['/audit/menu']); }
}
